import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import { CommonTableColumnsEnum } from '../../components/Table/types'
import { BIG_WIDGETS_ORDER, MEDIUM_WIDGETS_ORDER, SMALL_WIDGETS_ORDER } from '../../constants'
import { RootState } from '../../store'
import { LoadingStatusEnum } from '../../store/types'
import { SortDirectionCapital } from '../../types'
import { getInitialPaginationData, getInitialTableColumns } from '../../utils'
import { setTradeAiAnalysis } from '../TradingLog/TradingLogSlice'
import { Trade } from '../TradingLog/types'

import {
  BigWidgetsName,
  ClosedPositions,
  DashboardMetricsDTO,
  DashboardState,
  MediumWidgetsName,
  OpenPositions,
  SmallWidgetsName,
  DashboardRequestType,
  PositionsRequestType,
} from './types'

export const fetchDashboardWidgetsData = createAsyncThunk(
  'dashboard/fetchWidgets',
  async (widgetsRequestData: DashboardRequestType) => {
    try {
      const { DashboardService } = await import('./DashboardService')
      const { data } = await DashboardService.getWidgetsData(widgetsRequestData)
      return data
    } catch (error) {
      throw new Error(error.message)
    }
  },
)

export const fetchOpenPositionsData = createAsyncThunk(
  'dashboard/fetchOpenPositions',
  async (pagination: PositionsRequestType) => {
    try {
      const { DashboardService } = await import('./DashboardService')
      const { data } = await DashboardService.getOpenPositions(pagination)
      return data
    } catch (error) {
      throw new Error(error.message)
    }
  },
)

export const fetchClosedPositionsData = createAsyncThunk(
  'dashboard/fetchClosedPositions',
  async (pagination: PositionsRequestType) => {
    try {
      const { DashboardService } = await import('./DashboardService')
      const { data } = await DashboardService.getClosePositions(pagination)
      return data
    } catch (error) {
      throw new Error(error.message)
    }
  },
)

const storedSmallWidgetsOrder = localStorage.getItem(SMALL_WIDGETS_ORDER)
const fullSmallWidgetData = Object.keys(SmallWidgetsName).reduce((acc, smallWidgets) => {
  acc[smallWidgets as SmallWidgetsName] = true
  return acc
}, {} as Record<keyof typeof SmallWidgetsName, boolean>)

const initSmallWidgetsOrder = storedSmallWidgetsOrder
  ? JSON.parse(storedSmallWidgetsOrder)
  : fullSmallWidgetData

const storedMediumWidgetsOrder = localStorage.getItem(MEDIUM_WIDGETS_ORDER)
const fullMediumWidgetData = Object.keys(MediumWidgetsName).reduce((acc, mediumWidgets) => {
  acc[mediumWidgets as MediumWidgetsName] = true
  return acc
}, {} as Record<keyof typeof MediumWidgetsName, boolean>)

const initMediumWidgetsOrder = storedMediumWidgetsOrder
  ? JSON.parse(storedMediumWidgetsOrder)
  : fullMediumWidgetData

const storedBigWidgetsOrder = localStorage.getItem(BIG_WIDGETS_ORDER)
const fullBigWidgetData = Object.keys(BigWidgetsName).reduce((acc, bigWidgets) => {
  acc[bigWidgets as BigWidgetsName] = true
  return acc
}, {} as Record<keyof typeof BigWidgetsName, boolean>)

const initBigWidgetsOrder = storedBigWidgetsOrder
  ? { ...fullBigWidgetData, ...JSON.parse(storedBigWidgetsOrder) }
  : fullBigWidgetData

const initialOpenPosition = {
  data: [],
  pagination: {
    ...getInitialPaginationData<OpenPositions>(
      getInitialTableColumns().openPositions as (keyof OpenPositions)[],
    ),
    order: SortDirectionCapital.desc,
    orderBy: CommonTableColumnsEnum.openTime as keyof OpenPositions,
  },
}

const initialClosedPosition = {
  data: [],

  pagination: {
    ...getInitialPaginationData<ClosedPositions>(
      getInitialTableColumns().closedPositions as (keyof ClosedPositions)[],
    ),
    order: SortDirectionCapital.desc,
    orderBy: CommonTableColumnsEnum.closeTime as keyof OpenPositions,
  },
}

const initialState: DashboardState = {
  customize: false,
  widgetsData: {} as DashboardMetricsDTO,
  smallWidgetsList: initSmallWidgetsOrder,
  mediumWidgetsList: initMediumWidgetsOrder,
  bigWidgetsList: initBigWidgetsOrder,
  positions: {
    open: initialOpenPosition,
    closed: initialClosedPosition,
  },
  // Maybe we need this for some updates
  // refreshTrigger: 0,
  loading: LoadingStatusEnum.Idle,
  widgetsLoading: LoadingStatusEnum.Idle,
}

const dashboardSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    toggleCustomize(state) {
      state.customize = !state.customize
    },
    changeSmallWidgetsList(state, action) {
      state.smallWidgetsList = action.payload
      localStorage.setItem(SMALL_WIDGETS_ORDER, JSON.stringify(action.payload))
    },
    toggleSmallWidget(state, action) {
      state.smallWidgetsList[action.payload as SmallWidgetsName] =
        !state.smallWidgetsList[action.payload as SmallWidgetsName]
      localStorage.setItem(SMALL_WIDGETS_ORDER, JSON.stringify(state.smallWidgetsList))
    },
    changeMediumWidgetsList(state, action) {
      state.mediumWidgetsList = action.payload
      localStorage.setItem(MEDIUM_WIDGETS_ORDER, JSON.stringify(action.payload))
    },
    toggleMediumWidget(state, action) {
      state.mediumWidgetsList[action.payload as MediumWidgetsName] =
        !state.mediumWidgetsList[action.payload as MediumWidgetsName]
      localStorage.setItem(MEDIUM_WIDGETS_ORDER, JSON.stringify(state.mediumWidgetsList))
    },
    changeBigWidgetsList(state, action) {
      state.bigWidgetsList = action.payload
      localStorage.setItem(BIG_WIDGETS_ORDER, JSON.stringify(action.payload))
    },
    toggleBigWidget(state, action) {
      state.bigWidgetsList[action.payload as BigWidgetsName] =
        !state.bigWidgetsList[action.payload as BigWidgetsName]
      localStorage.setItem(BIG_WIDGETS_ORDER, JSON.stringify(state.bigWidgetsList))
    },
    updateOpenPositionsOrder: (state, action) => {
      state.positions.open.pagination.columns = action.payload
    },
    updateOpenPositionsPageSize: (state, action) => {
      state.positions.open.pagination.perPage = action.payload
    },
    updateOpenPositionsPage: (state, action) => {
      state.positions.open.pagination.page = action.payload
    },
    updateOpenPosOrderBy: (state, action) => {
      state.positions.open.pagination.page = 1
      state.positions.open.pagination.order = SortDirectionCapital.asc
      state.positions.open.pagination.orderBy = action.payload
    },
    updateOpenSortOrder: (state, action) => {
      state.positions.open.pagination.page = 1
      state.positions.open.pagination.order = action.payload
    },
    updateClosedPositionsOrder: (state, action) => {
      state.positions.closed.pagination.columns = action.payload
    },
    updateClosedPositionsPageSize: (state, action) => {
      state.positions.closed.pagination.perPage = action.payload
    },
    updateClosedPositionsPage: (state, action) => {
      state.positions.closed.pagination.page = action.payload
    },
    updateClosePosOrderBy: (state, action) => {
      state.positions.closed.pagination.page = 1
      state.positions.closed.pagination.order = SortDirectionCapital.asc
      state.positions.closed.pagination.orderBy = action.payload
    },
    updateCloseSortOrder: (state, action) => {
      state.positions.closed.pagination.page = 1
      state.positions.closed.pagination.order = action.payload
    },
    clearAllDashboard: (state) => {
      state.positions = initialState.positions
      state.widgetsData = initialState.widgetsData
      state.smallWidgetsList = initialState.smallWidgetsList
      state.mediumWidgetsList = initialState.mediumWidgetsList
      state.bigWidgetsList = initialState.bigWidgetsList
      state.customize = initialState.customize
      // state.refreshTrigger = initialState.refreshTrigger
    },
    // triggerDataRefresh: (state) => {
    //   state.refreshTrigger += 1
    // },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDashboardWidgetsData.pending, (state) => {
        state.widgetsLoading = LoadingStatusEnum.Pending
      })
      .addCase(fetchDashboardWidgetsData.fulfilled, (state, action) => {
        state.widgetsData = action.payload
        state.widgetsLoading = LoadingStatusEnum.Succeeded
      })
      .addCase(fetchDashboardWidgetsData.rejected, (state) => {
        state.widgetsLoading = LoadingStatusEnum.Failed
      })
      .addCase(fetchOpenPositionsData.fulfilled, (state, action) => {
        const open = action.payload

        state.positions.open = {
          data: open.data,
          pagination: { ...state.positions.open.pagination, ...open.meta },
        }
      })
      .addCase(fetchClosedPositionsData.fulfilled, (state, action) => {
        const closed = action.payload

        state.positions.closed = {
          data: closed.data,
          pagination: { ...state.positions.closed.pagination, ...closed.meta },
        }
      })
      .addCase(setTradeAiAnalysis, (state, action) => {
        state.positions.open.data = state.positions.open.data.map((trade) =>
          Number((trade as unknown as Trade).id) === action.payload.tradeId
            ? { ...trade, tradeAiAnalysis: action.payload.analysis }
            : trade,
        )
        state.positions.closed.data = state.positions.closed.data.map((trade) =>
          Number((trade as unknown as Trade).id) === action.payload.tradeId
            ? { ...trade, tradeAiAnalysis: action.payload.analysis }
            : trade,
        )
      })
  },
})

export const selectDashboardLoading = (state: RootState) => state.dashboard.loading
export const selectDashboardIsLoading = (state: RootState) =>
  state.dashboard.loading === LoadingStatusEnum.Pending
export const selectDashboardIsIdle = (state: RootState) =>
  state.dashboard.loading === LoadingStatusEnum.Idle

export const selectors = {
  loading: selectDashboardLoading,
  isLoading: selectDashboardIsLoading,
  isIdle: selectDashboardIsIdle,
}

const DashboardSliceReducer = dashboardSlice.reducer

export const {
  toggleCustomize,
  changeSmallWidgetsList,
  changeMediumWidgetsList,
  toggleMediumWidget,
  toggleSmallWidget,
  changeBigWidgetsList,
  toggleBigWidget,
  updateOpenPositionsOrder,
  updateOpenPositionsPageSize,
  updateOpenPositionsPage,
  updateClosedPositionsOrder,
  updateClosedPositionsPageSize,
  updateClosedPositionsPage,
  updateOpenPosOrderBy,
  updateOpenSortOrder,
  updateCloseSortOrder,
  updateClosePosOrderBy,
  clearAllDashboard,
  // triggerDataRefresh,
} = dashboardSlice.actions
export default DashboardSliceReducer
